import React, { useEffect, useImperativeHandle, useRef, useState } from 'react';
import { AnalyticEvents } from '@constants/index';
import { datadogRum } from '@datadog/browser-rum';
import { Button } from '@carvana/core-helios-button';
import { useRouter, ROUTES } from '../../providers/router';
import { useUserSelections } from '../../providers/user-selections';
import { Analytics } from '../../../../services/analytics-service';
import styles from './vehicle-concern-details.module.css';
import globalStyles from '../../service-and-repair.module.css';

const MAX_CHARS = 3000;

interface TextAreaProps {
  onHasValue: (hasValue: boolean) => void;
  onFocus?: () => void;
  initialValue?: string;
}

export interface TextAreaRef {
  getValue: () => string;
  focus: () => void;
}

const TextArea = React.forwardRef<TextAreaRef, TextAreaProps>(({ onHasValue, onFocus, initialValue = '' }, ref) => {
  const [value, setValue] = useState(initialValue);
  const textAreaEl = useRef<HTMLTextAreaElement>(null);

  useImperativeHandle(ref, () => ({
    getValue: () => value,
    focus: () => {
      if (textAreaEl.current) {
        textAreaEl.current.focus();
      }
    },
  }));

  const handleChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = event.target.value;

    if (newValue.length <= MAX_CHARS) {
      setValue(newValue);
      onHasValue(newValue.trim().length > 0);
    }
  };

  return (
    <textarea
      ref={textAreaEl}
      rows={4}
      value={value}
      onChange={handleChange}
      onFocus={onFocus}
      className={styles['textarea']}
      placeholder=""
      aria-label="Describe your vehicle issue"
      aria-required="true"
      maxLength={MAX_CHARS}
    />
  );
});

TextArea.displayName = 'TextArea';

export const VehicleConcernDetails = () => {
  const { completeFormStep } = useRouter();
  const { userSelections, updateUserSelections } = useUserSelections();
  const textAreaRef = useRef<TextAreaRef>(null);
  const hasTrackedFocus = useRef(false);
  const [isButtonDisabled, setButtonDisabled] = useState(
    !userSelections.details || userSelections.details.trim().length === 0
  );
  const [showRequiredError, setShowRequiredError] = useState(false);

  const handleTextAreaFocus = () => {
    if (!hasTrackedFocus.current) {
      hasTrackedFocus.current = true;
      datadogRum.addAction(AnalyticEvents.vehicleConcernDetailsTextboxFocused, { flow: 'non-cosmetic' });
    }
  };

  const handleNextRoute = () => {
    const issueDescription = textAreaRef.current?.getValue();

    if (!issueDescription || issueDescription.trim().length === 0) {
      setShowRequiredError(true);
      if (textAreaRef.current) {
        textAreaRef.current.focus();
      }
      return;
    }

    updateUserSelections({ details: issueDescription });

    Analytics.trackEvent(AnalyticEvents.vehicleConcernDetailsClickedNext, {
      detailsLength: issueDescription.length,
    });
    datadogRum.addAction(AnalyticEvents.vehicleConcernDetailsClickedNext, {
      detailsLength: issueDescription.length,
    });

    completeFormStep(ROUTES.VEHICLE_CONCERN_DETAILS);
  };

  useEffect(() => {
    Analytics.trackEvent(AnalyticEvents.vehicleConcernDetailsViewed);
    datadogRum.addAction(AnalyticEvents.vehicleConcernDetailsViewed);

    // Set the height of the modal content to 100% - this will affect other parts of the modal so we only needed it here
    const modalContent = document.getElementById('modal-content');
    if (modalContent) {
      modalContent.style.height = '100%';
    }

    if (textAreaRef.current) {
      textAreaRef.current.focus();
    }

    return () => {
      // Reset the height of the modal content when the component unmounts
      if (modalContent) {
        modalContent.style.height = 'auto';
      }
    };
  }, []);

  return (
    <div className={globalStyles['page-wrapper']} style={{ height: '100%' }}>
      <div className={globalStyles['page-header']}>
        <h2>Tell us more about the issue</h2>
        <p>
          Describe what you're experiencing, or add any other details we should know. Sharing this helps our team
          evaluate the issue and improve how we support you.
        </p>
      </div>

      <div className={globalStyles['form-wrapper']}>
        <TextArea
          ref={textAreaRef}
          onFocus={handleTextAreaFocus}
          onHasValue={(hasValue) => {
            setButtonDisabled(!hasValue);
            setShowRequiredError(false);
          }}
          initialValue={userSelections.details}
        />
        {showRequiredError && <div className={styles['required-error']}>This field is required</div>}
      </div>

      <div className={globalStyles['button-wrapper']}>
        <Button type="button" disabled={isButtonDisabled} onClick={handleNextRoute}>
          Next
        </Button>
      </div>
    </div>
  );
};

export default VehicleConcernDetails;
